<script setup lang="ts">
import type { PropertyOnPage } from '@@/shared/property/api-types';

defineProps<{
  property: PropertyOnPage
}>();

const listElement = ref<HTMLElement>();
const hasOverflow = ref(false);
const showMore = ref(false);
const { locale, t } = useI18n();
const displayShowMore = computed(() => hasOverflow.value || showMore.value);

useResizeObserver(listElement, (entries) => {
  if (!listElement.value) return;
  const textEntry = entries.find(e => e.target === listElement.value);
  if (!textEntry) return;
  // having borderBoxSize avoid us a getBoundingClientRect() call
  // • support isn't 100% (Safari…)
  //   https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserverEntry/borderBoxSize
  let height: number;
  const { scrollHeight } = listElement.value;
  if (textEntry.borderBoxSize) {
    // in Chrome & Edge borderBoxSize is an Array
    // in Firefox not…
    const sizes = textEntry.borderBoxSize[0] || textEntry.borderBoxSize;
    // “blockSize” exists in ResizeObserverSize
    // https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserverSize
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    height = sizes.blockSize;
  }
  else {
    height = listElement.value.offsetHeight;
  }
  // this is how in JS we can check if there is an overflow
  // Round is needed as we might have a sub-pixel value like 32.983333587646484
  hasOverflow.value = Math.round(height) < Math.round(scrollHeight);
});

function formatHeatingPowerSupply(translations: Array<string> | undefined): string {
  if (translations == null) return '';
  const formatter = new Intl.ListFormat(locale.value, {
    style: 'long',
    type: 'conjunction',
  });
  const heatingPowerSupplyList = translations.map(translation => t(translation));
  return formatter.format(heatingPowerSupplyList);
}

// NOTE: we can't use a “display: flex” in the <ul />
// This would break the line-clamp
</script>

<template>
  <section class="pt-2xl">
    <IadTitle
      tag="h3"
      :text="$t(`property.common.characteristics`)"
      class="mb-s"
    />
    <ul
      ref="listElement"
      :class="{ 'line-clamp-[4]': !showMore }"
    >
      <li
        v-for="mainFeature of property.mainFeatures"
        :key="mainFeature.type"
        class="inline-block whitespace-nowrap pb-xs before:content-['_•_'] before:inline-block before:px-[6px] first:before:content-none"
      >
        <Icon
          :name="mainFeature.icon"
          class="size-l inline-block"
        />
        {{ $t(`property.feature.${mainFeature.type}`, { count: mainFeature.value }) }}
      </li>
      <li
        v-for="secondaryFeature of property.secondaryFeatures"
        :key="secondaryFeature.type"
        class="inline-block whitespace-nowrap pb-xs before:content-['_•_'] before:inline-block before:px-[6px] first:before:content-none"
      >
        <I18nT
          v-if="secondaryFeature.translationKey?.length"
          tag="span"
          keypath="property.feature.heating_power_supply"
        >
          <template #powerSupply>
            <span
              class="lowercase"
              v-text="formatHeatingPowerSupply(secondaryFeature?.translationKey)"
            />
          </template>
        </I18nT>

        <span
          v-else
          v-text="$t(`property.feature.${secondaryFeature.type}`, { count: secondaryFeature.value, totalCount: secondaryFeature.secondaryValue })"
        />
      </li>
    </ul>
    <IadLinkMoreLess
      v-if="displayShowMore"
      v-model="showMore"
      class="mt-s"
    />
  </section>
</template>
